// 사용자 정보 타입 정의
import { CommonUtils } from './commonUtils';
import { getApiV1Url } from '../config/api';

export interface UserInfo {
  accountCode: string;
  userId: string;
  userType: 'root' | 'iam';
  loginId: string; // 로그인에 사용한 ID
}

// 로컬 스토리지 키
const USER_INFO_KEY = 'userInfo';

// 사용자 정보 저장
export const saveUserInfo = (userInfo: UserInfo): void => {
  localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
};

// 사용자 정보 가져오기
export const getUserInfo = (): UserInfo | null => {
  try {
    const stored = localStorage.getItem(USER_INFO_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('사용자 정보를 가져오는 중 오류가 발생했습니다:', error);
    return null;
  }
};

// 사용자 정보 삭제 (로그아웃 시)
export const clearUserInfo = (): void => {
  localStorage.removeItem(USER_INFO_KEY);
};

// 모든 저장된 정보 삭제 (로그아웃 시)
export const clearAllStoredData = (): void => {
  // 사용자 정보 삭제
  localStorage.removeItem(USER_INFO_KEY);
  
  // 프로젝트 정보 삭제
  localStorage.removeItem('selectedProject');
  
  // 멤버십 뷰 타입 삭제 (선택적)
  localStorage.removeItem('membershipViewType');
  
  // 기타 저장된 정보가 있다면 여기에 추가
  // 예: 필터 설정, 검색어 등
};

// 로그인 상태 확인
export const isLoggedIn = (): boolean => {
  return getUserInfo() !== null;
};

// 프로젝트 코드 가져오기 (localStorage에서)
export const getProjectCode = (): string => {
  const savedProject = localStorage.getItem('selectedProject');
  if (!savedProject) {
    throw new Error('선택된 프로젝트가 없습니다. 프로젝트를 먼저 선택해주세요.');
  }
  
  const parsedProject = JSON.parse(savedProject);
  if (!parsedProject.code) {
    throw new Error('프로젝트 코드가 없습니다. 유효한 프로젝트를 선택해주세요.');
  }

  return parsedProject.code;
};

// 비밀번호 변경 API
export const changePassword = async (
  accountCode: string,
  userId: string,
  userType: 'root' | 'iam',
  oldPassword: string,
  newPassword: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Account-Code': accountCode,
      'X-User-Id': userType === 'root' ? accountCode : userId
    };

    let url: string;
    let body: any;

    if (userType === 'root') {
      // 루트 사용자 비밀번호 변경
      url = getApiV1Url(`/accounts/${accountCode}/password`);
      body = {
        oldPassword,
        newPassword
      };
    } else {
      // IAM 사용자 비밀번호 변경 - 사용자가 제공한 엔드포인트 사용
      url = getApiV1Url(`/accounts/${accountCode}/users/${userId}/password`);
      body = {
        oldPassword,
        newPassword
      };
    }

    console.log('비밀번호 변경 API 호출:', { url, accountCode, userId, userType });

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      const statusCode = response.status;
      const errorData = await response.json().catch(() => ({}));
      
      // CommonUtils의 HTTP 에러 메시지 처리 함수 사용
      const errorMessage = CommonUtils.getHttpErrorMessage(statusCode, errorData);
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('비밀번호 변경 API 응답:', result);
    
    return {
      success: true,
      message: result.message || '비밀번호가 성공적으로 변경되었습니다.'
    };
  } catch (error: any) {
    console.error('비밀번호 변경 중 오류 발생:', error);
    throw error;
  }
};